import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Box, Button, SelectChangeEvent } from "@mui/material";
import styled from "styled-components";
import { useAppSelector } from "../../store/hooks";
import { EditActionCell } from "../../components/DataTable/customcell/EditActionCell";
import CropTypeSelector from "./CropTypeSelector";
import SearchBox from "./SearchBox";
import EditModal from "./EditModal";
import useSeedCosts from "../../hooks/useSeedCosts";
import { RootState } from "../../store";
import { Add, Launch } from "@mui/icons-material";
import { SeedCostT } from "../../types/addCost";

interface SeedVariety extends Omit<SeedCostT, "cropId" | "cropYear"> {
  id: number;
  cropName: string;
  variety: string;
  discountedPrice: number;
  perSeed: number;
  bagBushel: string;
  perBushel: number | null;
}

const StyledGrid = styled(Grid)`
  .k-column-title {
    min-width: 0;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 1;
    overflow: hidden;
    font-weight: bold; /* Make the font bold */
    font-family: "Roboto";
  }
  ,
  th {
    border-inline-start-width: 0px !important;
  }
  th > .k-cell-inner > .k-link {
    border-right: 2px solid var(--kendo-color-border);
    justify-content: center;
  }
  ,
  th > .k-cell-inner,
  .k-grid-header-wrap {
    background-color: white;
    height: 55px;
  }
  ,
  .k-table-row,
  .k-table-alt-row,
  .k-grid-table-wrap {
    background-color: #f3f4f6 !important;
  }
  ,
  .k-table-td {
    border-bottom: 1px solid var(--kendo-color-border);
    border-left: none;
    border-right: none;
    font-family: "Roboto";
    text-align: center;
  }
`;

const SeedsVarieties: React.FC = () => {
  const [page, setPage] = useState({ skip: 0, take: 20 });
  const [selectedCropType, setSelectedCropType] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [editItem, setEditItem] = useState<any>(null);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const excelExportRef = useRef<ExcelExport | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  const { loading, error, fetchSeedCosts } = useSeedCosts();
  const { seedsCostArray } = useAppSelector(
    (state: RootState) => state.seedsdropdown
  );

  const createEmptySeedCost = (): SeedVariety => ({
    id: 0,
    cropName: "",
    variety: "",
    discountedPrice: 0,
    perSeed: 0,
    bagBushel: "",
    perBushel: null,
    priceUnit: "",
    periods: [],
  });

  useEffect(() => {
    fetchSeedCosts(Number(selectedCropType));
  }, [selectedCropType]);

  useEffect(() => {
    setPage((prev) => ({ ...prev, skip: 0 }));
  }, [searchQuery, selectedCropType]);

  const handleExport = () => {
    if (excelExportRef.current) {
      excelExportRef.current.save();
    }
  };

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const handleCropTypeChange = (event: SelectChangeEvent<string>) => {
    const selectedCrop = event.target.value;
    setSelectedCropType(selectedCrop);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleEditClick = (item: SeedVariety) => {
    setEditItem(item);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditItem(null);
    setIsCreate(false);
    fetchSeedCosts(Number(selectedCropType));
  };

  const handleEditSave = () => {
    setIsEditModalOpen(false);
    setEditItem(null);
    setIsCreate(false);
  };

  const filteredData = seedsCostArray.filter((item: any) => {
    const matchesSearchQuery = item.variety
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesSearchQuery;
  });

  const gridColumns: any[] = [
    {
      field: "cropName",
      title: "Crop Type",
      width: "180px",
    },
    {
      field: "variety",
      title: "Variety",
      width: "180px",
    },
    {
      field: "priceUnit",
      title: "Price Unit",
      width: "180px",
    },
    {
      field: "formattedPeriods",
      title: "Price Periods",
      width: "250px",
    },
    {
      title: "Actions",
      width: "100px",
      cells: {
        data: (props: any) => (
          <EditActionCell {...props} onEditClick={handleEditClick} />
        ),
      },
    },
  ];

  const onItemChange = (e: any) => {
    const { field, value, dataItem } = e;
    const updatedData = tableData.map((item: any) => {
      if (item.id === dataItem.id) {
        const updatedRow = {
          ...item,
        };
        updatedRow[field] = value;
        return updatedRow;
      }
      return item;
    });
    setTableData(updatedData);
  };

  const deletePricePeriod = (dataItem: any) => {
    setTableData(tableData.filter((item: any) => item.id !== dataItem.id));
  };

  const onAddSeedClick = () => {
    setIsCreate(true);
    setEditItem(createEmptySeedCost());
    setIsEditModalOpen(true);
  };

  return (
    <Box display={"flex"} p={3} gap={2} flexDirection={"column"}>
      <Box display={"flex"} gap={2}>
        <CropTypeSelector
          selectedCropType={selectedCropType}
          handleCropTypeChange={handleCropTypeChange}
        />
        <SearchBox
          placeholder={"Search Varieties"}
          searchQuery={searchQuery}
          handleSearchChange={handleSearchChange}
        />
        <Button variant="contained" startIcon={<Add />} onClick={onAddSeedClick}>
                  Add Seed Cost
                </Button>
        <Button
          variant="contained"
          startIcon={<Launch />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>
      {loading && <p>Loading...</p>}
      {error && <p>Error: {error}</p>}
      <Box>
        <ExcelExport data={filteredData} ref={excelExportRef}>
          <StyledGrid
            data={filteredData}
            pageable={{ pageSizes: [10, 20, 30], info: true }}
            total={filteredData.length}
            skip={page.skip}
            take={page.take}
            autoProcessData={true}
            onPageChange={handlePageChange}
            scrollable="scrollable"
          >
            {gridColumns.map((columnProps: any) => (
              <GridColumn
                key={columnProps.field || columnProps.title}
                {...columnProps}
                title={columnProps.title}
              />
            ))}
          </StyledGrid>
        </ExcelExport>
      </Box>
      {isEditModalOpen && (
        <EditModal
          isOpen={isEditModalOpen}
          editItem={editItem}
          edit={editItem}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          onItemChange={onItemChange}
          deletePricePeriod={deletePricePeriod}
          tableData={tableData}
          isCreate={isCreate}
        />
      )}
    </Box>
  );
};

export default SeedsVarieties;
